import { useRef, useCallback, useEffect } from "react";
import { isDomesticOfficialSeedance } from '../utils/domesticSeedanceCapabilities';
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { urlToBase64 } from "../utils/fileUtils";
import { extractTaskResultUrl, submitTask, startTaskPolling, queryImageTaskStatus, runTaskToCompletion } from "../services/taskService";
import {
  isDurableVideoPollingProvider,
  submitVideoTask,
  startPolling as startVideoPolling,
} from "../services/videoService";
import { useToast } from "../context/ToastContext";
import { resolveGuaVideoRefMarkers, stripRefMarkers } from "../utils/mentionUtils";
import { applyGuaVoiceBindingPrompt, getGuaRefUrl } from "../utils/guaVoiceBindings";
import { syncGeneratedImageForRow } from "../utils/storyboardImageSync";
import {
  getProjectVideoProvider,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoProvider as normalizeProjectVideoProvider,
  normalizeVideoResolutionForProvider,
} from "../utils/projectRuntime";
import { getEffectiveAssetMeta } from "../utils/guaVideoAssetStatus";
import { preflightVideoAssetContents } from "../utils/videoAssetPreflight";
import { getDreaminaProxyOriginalUrl, requireDreaminaProxyOriginalUrl } from "../utils/dreaminaProxyReferences";
import {
  getSequenceShotImage,
  normalizeSequenceVideoDuration,
  resolveSequenceVideoRuntime,
} from "../utils/sequenceVideoCapabilities";
import {
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  normalizeWanxImageQualityTier,
} from "../utils/wanxImageCapabilities";
import {
  resolveWanxVideoMediaMarkers,
  isWanxVideoModel,
  normalizeWanxVideoDuration,
  normalizeWanxVideoModel,
  validateWanxVideoRequest,
  WANX_VIDEO_MEDIA_TYPES,
} from "../utils/wanxVideoCapabilities";

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getImageUrlFromResult = (result) => {
  return extractTaskResultUrl(result) || "";
};

const getOpenAiGenerationFromResult = (result) => {
  if (!isPlainObject(result)) return null;
  return result.openai_generation || result.openaiGeneration || null;
};

const getContinuityFromResult = (result) => {
  if (!isPlainObject(result)) return null;
  return result.continuity || null;
};

const buildDirectorContextPayload = (currentRow) => {
  const context = isPlainObject(currentRow?.director_context)
    ? { ...currentRow.director_context }
    : {};

  if (Array.isArray(currentRow?.director_notes) && currentRow.director_notes.length) {
    context.notes = currentRow.director_notes;
  }
  if (currentRow?.director_image_prompt) {
    context.image_prompt_advice = currentRow.director_image_prompt;
  }
  if (currentRow?.director_video_prompt) {
    context.video_prompt_advice = currentRow.director_video_prompt;
  }
  if (isPlainObject(currentRow?.director_suggested_refs)) {
    context.suggested_refs = currentRow.director_suggested_refs;
  }

  return Object.keys(context).length ? context : null;
};

const getReferenceUrlsFromRefs = (...groups) => {
  const seen = new Set();
  const urls = [];
  groups.flat().forEach((ref) => {
    const url = ref?.base64 || ref?.url || ref?.imageUrl;
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
  });
  return urls;
};

const isSendableMediaUrl = (url) =>
  typeof url === "string" && /^(https?:\/\/|asset:\/\/)/i.test(url.trim());

const normalizeVideoProvider = (provider) => {
  if (!provider) return "vidu";
  if (isWanxVideoModel(provider)) return "wanx";
  if (provider === "kling" || provider === "klingService") return "vidu";
  return provider;
};

const getRowVideoProvider = (row = {}) => normalizeVideoProvider(
  row.video_provider || row.videoProvider || row.video_model || row.videoModel,
);

const WANX_STORYBOARD_MODE = Object.freeze({
  text_to_video: "text2video",
  first_frame: "img2video",
  first_last_frame: "start-end",
  reference_to_video: "reference",
});

const currentProjectIdFromPath = () => window.location.pathname.match(/\/project\/(\d+)/)?.[1] || "";
const isGuaLikeVideoProvider = isSeedanceVideoProvider;

const normalizeVideoModel = (provider, model) => {
  if (provider === "wanx") {
    return normalizeWanxVideoModel(model);
  }
  if (provider === "vidu" && (!model || String(model).startsWith("kling-"))) {
    return "viduq3-pro";
  }
  return model;
};

/**
 * 自定义 Hook: 管理 AI 图片和视频生成功能
 *
 * @param {Object} options - 配置选项
 * @param {Function} options.setRows - 更新 rows 状态的函数
 * @param {Function} options.performAutoSave - 自动保存的回调函数
 * @returns {Object} AI 生成相关的方法
 */
export const useAIGeneration = ({
  setRows,
  performAutoSave,
  videoEnabled = true,
  projectId,
  episodeId,
}) => {
  const toast = useToast();
  const videoEnabledRef = useRef(videoEnabled);
  videoEnabledRef.current = videoEnabled;
  // 存储所有轮询定时器的 Ref，Key: "img_${shotId}" / "vid_${shotId}" 等, Value: intervalId 或 stop 函数
  const pollIntervalsRef = useRef({});

  /**
   * 清理所有轮询定时器
   */
  const clearAllPollIntervals = useCallback(() => {
    Object.entries(pollIntervalsRef.current).forEach(([key, val]) => {
      if (typeof val === 'function') {
        val(); // stop 函数（来自 startTaskPolling）
      } else if (val) {
        clearInterval(val); // intervalId（来自视频轮询）
      }
    });
    pollIntervalsRef.current = {};
  }, []);

  /**
   * 清理指定索引的轮询定时器
   * @param {string|number} index - 行索引或 key
   */
  const clearPollInterval = useCallback((index) => {
    const val = pollIntervalsRef.current[index];
    if (val) {
      if (typeof val === 'function') {
        val();
      } else {
        clearInterval(val);
      }
      delete pollIntervalsRef.current[index];
    }
  }, []);

  /**
   * 暂停可恢复的视频生成轮询。这里只停止浏览器查询，不取消后端任务、
   * 不删除 taskId，也不把任务标记为失败；重新显示视频栏时可以恢复。
   * prompt_ 任务没有持久 taskId，因此允许其静默完成并自动保存，避免孤儿任务。
   */
  const suspendVideoPolling = useCallback(() => {
    Object.keys(pollIntervalsRef.current).forEach((key) => {
      if (key.startsWith("vid_") || key.startsWith("seq_vid_")) {
        clearPollInterval(key);
      }
    });
  }, [clearPollInterval]);

  useEffect(() => {
    if (!videoEnabled) suspendVideoPolling();
  }, [videoEnabled, suspendVideoPolling]);

  // 组件卸载时清理所有定时器
  useEffect(() => {
    return () => {
      clearAllPollIntervals();
    };
  }, [clearAllPollIntervals]);

  /**
   * 构建参考资源列表
   * @param {string[]} ids - 资源 ID 列表
   * @param {Array} options - 资源选项列表
   * @param {number[]} variantIndexes - 变体索引列表（-1=主体图，0+=变体索引）
   * @returns {Array} 包含 name 和 base64 的参考资源列表
   */
  const buildRefList = useCallback((ids, options, variantIndexes = null) => {
    if (!ids || !ids.length) return [];
    // options 可能为 undefined，需要检查
    if (!options || !Array.isArray(options)) return [];
    const list = [];
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const item = options.find((opt) => opt.id === id);
      if (!item) continue;

      // 获取变体索引（默认 -1 表示主体图）
      const variantIndex = variantIndexes && variantIndexes[i] !== undefined ? variantIndexes[i] : -1;

      let imageUrl = null;

      // 根据变体索引选择图片
      if (variantIndex === -1) {
        // 使用主体图
        imageUrl = item.imageUrl;
      } else if (item.variations && Array.isArray(item.variations) && variantIndex >= 0 && variantIndex < item.variations.length) {
        // 使用变体图
        const variant = item.variations[variantIndex];
        imageUrl = variant.imageUrl;
      } else {
        // 变体索引无效，回退到主体图
        imageUrl = item.imageUrl;
      }

      if (imageUrl) {
        const variant = variantIndex >= 0 && item.variations?.[variantIndex] ? item.variations[variantIndex] : null;
        list.push({
          name: item.name,  // 只使用主体名称，不带变体名
          base64: imageUrl,  // 但图片使用变体的 URL
          url: imageUrl,
          imageUrl,
          sourceAssetId: item.id,
          guaVideoAssetId: variant?.guaVideoAssetId || item.guaVideoAssetId,
          guaVideoAssetStatus: variant?.guaVideoAssetStatus || item.guaVideoAssetStatus,
          engGuaVideoAssetId: variant?.engGuaVideoAssetId || item.engGuaVideoAssetId,
          engGuaVideoAssetStatus: variant?.engGuaVideoAssetStatus || item.engGuaVideoAssetStatus,
          guaOminiAssetId: variant?.guaOminiAssetId || item.guaOminiAssetId,
          guaOminiAssetStatus: variant?.guaOminiAssetStatus || item.guaOminiAssetStatus,
          dreaminaProxyAssetId: variant?.dreaminaProxyAssetId || item.dreaminaProxyAssetId,
          dreaminaProxyAssetStatus: variant?.dreaminaProxyAssetStatus || item.dreaminaProxyAssetStatus,
          voiceSampleUrl: item.voiceSampleUrl,
          voiceSampleName: item.voiceSampleName,
          voiceBoundAt: item.voiceBoundAt,
        });
      }
    }
    return list;
  }, []);

  /**
   * 轮询任务状态（视频专用，保留原有逻辑）
   * @param {string} provider - 服务提供商 (vidu/minimax/guavideo 等)
   * @param {string} taskId - 任务 ID
   * @param {number} index - 行索引
   * @param {Function} onSuccess - 成功回调
   * @param {Function} onError - 失败回调
   */
  const pollTaskStatus = useCallback((provider, taskId, index, onSuccess, onError) => {
    if (!videoEnabledRef.current) return () => {};
    // 清除该索引之前的轮询
    clearPollInterval(index);

    let errorCount = 0;
    const pollIntervalMs = normalizeVideoProvider(provider) === "wanx" ? 15000 : 3000;

    const intervalId = setInterval(async () => {
      if (!videoEnabledRef.current) {
        clearPollInterval(index);
        return;
      }
      try {
        const statusData = await authenticatedFetch(
          `${API_HOST}/api/video/status?provider=${provider}&taskId=${taskId}`
        );

        // 重置错误计数
        errorCount = 0;

        if (statusData.status === "success" || statusData.status === "completed") {
          clearPollInterval(index);
          onSuccess?.(statusData);
        } else if (statusData.status === "failed" || statusData.status === "error") {
          clearPollInterval(index);
          onError?.(statusData);
        }
        // 其他状态 (pending, processing) 继续轮询
      } catch (e) {
        console.error("轮询任务状态失败:", e);
        errorCount++;
        // 连续失败 3 次则停止
        if (errorCount >= 3) {
           clearPollInterval(index);
           onError?.({ status: "failed", errorMessage: "网络请求失败，已停止轮询" });
        }
      }
    }, pollIntervalMs);

    pollIntervalsRef.current[index] = intervalId;
  }, [clearPollInterval]);

  /**
   * 轮询后端视频任务状态（新模式：后端负责轮询服务商，前端只查后端）
   * @param {number|null} shotId - shot 数据库 ID（有则用 shotId 模式）
   * @param {string} providerTaskId - 服务商任务 ID
   * @param {string} provider - 服务商名称
   * @param {Object} options - onSuccess(videoUrl), onError(msg)
   * @returns {Function} stop
   */
  const startVideoStatusPolling = useCallback((shotId, providerTaskId, provider, options = {}) => {
    if (!videoEnabledRef.current) return () => {};
    const { onSuccess, onError } = options;
    let isRunning = true;
    let timeoutId = null;
    let errorCount = 0;
    const startTime = Date.now();
    const MAX_POLL_TIME = 120 * 60 * 1000; // ✅ 120分钟总超时
    const usesDurablePolling = isDurableVideoPollingProvider(provider);
    const pollIntervalMs = usesDurablePolling ? 15000 : 5000;

    const poll = async () => {
      if (!isRunning || !videoEnabledRef.current) return;

      // ✅ 增加总超时检查，防止无限轮询
      if (Date.now() - startTime > MAX_POLL_TIME) {
        isRunning = false;
        onError?.("视频生成超时（120分钟），已停止轮询");
        return;
      }

      try {
        let url = `${API_HOST}/api/video/status?provider=${provider}&taskId=${providerTaskId}`;
        if (shotId) url += `&shotId=${shotId}`;

        const data = await authenticatedFetch(url);
        if (!isRunning || !videoEnabledRef.current) return;
        errorCount = 0;

        const status = data.status;
        if (status === "success" || status === "completed") {
          isRunning = false;
          onSuccess?.(data.videoUrl, data.lastFrameImage);
        } else if (status === "failed" || status === "error") {
          isRunning = false;
          onError?.(data.errorMessage || "视频生成失败");
        } else {
          // polling / processing，继续等待
          timeoutId = setTimeout(poll, pollIntervalMs);
        }
      } catch (e) {
        if (!isRunning || !videoEnabledRef.current) return;
        errorCount++;
        if (usesDurablePolling && Date.now() - startTime <= MAX_POLL_TIME) {
          timeoutId = setTimeout(poll, pollIntervalMs);
        } else if (errorCount >= 5) {
          isRunning = false;
          onError?.("网络连接中断，已停止轮询");
        } else {
          timeoutId = setTimeout(poll, pollIntervalMs);
        }
      }
    };

    if (usesDurablePolling) timeoutId = setTimeout(poll, pollIntervalMs);
    else poll();
    return () => {
      isRunning = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  /**
   * 页面加载时恢复 polling 状态的视频任务
   * 在分镜列表加载完 rows 后调用
   */
  const resumeVideoPolling = useCallback((rows) => {
    if (!videoEnabledRef.current) return;
    rows.forEach((row) => {
      if (row.video_task_status === "polling" && row.video_task_id && row.id) {
        const shotId = row.id;
        const pollKey = `vid_${shotId}`;
        if (pollIntervalsRef.current[pollKey]) return;

        const stop = startVideoStatusPolling(shotId, row.video_task_id, getRowVideoProvider(row), {
          onSuccess: (videoUrl, lastFrameImage) => {
            delete pollIntervalsRef.current[pollKey];
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              n[idx] = {
                ...n[idx],
                isGeneratingVideo: false,
                generated_video: videoUrl,
                last_frame_image: lastFrameImage || null,
                video_task_status: "success",
                video_task_error: null,
              };
              performAutoSave?.(n);
              return n;
            });
          },
          onError: (errorMessage) => {
            delete pollIntervalsRef.current[pollKey];
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              n[idx] = {
                ...n[idx],
                isGeneratingVideo: false,
                video_task_status: "failed",
                video_task_error: errorMessage || "视频生成失败",
              };
              performAutoSave?.(n);
              return n;
            });
          },
        });

        pollIntervalsRef.current[pollKey] = stop;
      }
    });
  }, [startVideoStatusPolling, setRows, performAutoSave]);
  const handleGenerateImage = useCallback(async (
    index,
    rows,
    currentStyle,
    currentRatio,
    buildRefListFn,
    characterOptions,
    sceneOptions,
    propOptions
  ) => {
    const currentRow = rows[index];
    if (!currentRow) {
      console.error(`[GenerateImage] index=${index} 对应的 row 不存在, rows.length=${rows.length}`);
      throw new Error(`分镜 ${index + 1} 数据不存在`);
    }
    const shotId = currentRow.id;
    const promptText =
      currentRow.image_prompt || currentRow.visual_content || currentRow.action;
    console.log(`[GenerateImage] index=${index}, shotId=${shotId}, promptText="${(promptText || '').substring(0, 50)}"`);
    if (!promptText) {
      console.warn(`[GenerateImage] index=${index} 没有 prompt，跳过`);
      toast.warning(`分镜 ${index + 1} 没有画面描述，已跳过`);
      return;
    }

    // 设置生成中状态 - 立即返回新状态，确保 UI 同步更新
    setRows((prev) => {
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx === -1) return prev; // 返回 prev 而不是 n，避免不必要的重渲染
      n[idx] = { ...n[idx], isGenerating: true };
      return n;
    });

    try {
      // 1. 构建参考资源（支持变体索引）
      const refBuilder = buildRefListFn || buildRefList;
      const characterRefs = refBuilder(
        currentRow.refCharacterIds,
        characterOptions,
        currentRow.refCharacterVariantIndexes
      );
      const sceneRefs = refBuilder(
        currentRow.refSceneIds,
        sceneOptions,
        currentRow.refSceneVariantIndexes
      );
      const propRefs = refBuilder(
        currentRow.refPropIds,
        propOptions,
        currentRow.refPropVariantIndexes
      );

      const directorEnabled = Boolean(currentRow.director_enabled);
      const directorContext = directorEnabled ? buildDirectorContextPayload(currentRow) : null;
      const imageProvider = getCurrentImageProvider();
      const requestedQualityTier = currentRow.image_quality_tier
        || currentRow.imageQualityTier
        || localStorage.getItem("image_quality_tier")
        || "4K";
      assertWanxImageInputLimit([
        ...characterRefs,
        ...sceneRefs,
        ...propRefs,
        currentRow.sketch_base64,
      ], imageProvider);

      // 2. 构造请求体
      const payload = {
        prompt: promptText,
        style: currentStyle,
        aspectRatio: currentRatio,
        characterRefs,
        sceneRefs,
        propRefs,
        continuityMode: null,
        ref_shot_index: null,
        continuity: null,
        openai_generation: null,
        directorEnabled,
        directorContext,
        sketchBase64: currentRow.sketch_base64,
        prevImageBase64: null,
        imageQualityTier: normalizeWanxImageQualityTier(requestedQualityTier, imageProvider),
      };

      // 3. 提交异步任务（传入 shotId 用于持久化）
      console.log(`[GenerateImage] index=${index} 开始提交任务, shotId=${shotId}, director=${directorEnabled}`);
      const { taskId } = await submitTask("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
        provider: imageProvider,
        styleAnchor: currentStyle,
        shotId: shotId, // 新增：用于后端持久化任务状态
      });
      console.log(`[GenerateImage] index=${index} 任务提交成功, taskId=${taskId}`);

      // 4. 开始轮询（用 shotId 作为 pollKey，避免 index 偏移）
      const pollKey = `img_${shotId}`;
      clearPollInterval(pollKey);

      const stop = startTaskPolling(taskId, {
        onSuccess: (result) => {
          const imageUrl = getImageUrlFromResult(result);
          const openaiGeneration = getOpenAiGenerationFromResult(result);
          const continuity = getContinuityFromResult(result);
          delete pollIntervalsRef.current[pollKey];
          if (!imageUrl) {
            toast.error("生成成功但未返回图片地址", 0);
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              n[idx] = { ...n[idx], isGenerating: false };
              return n;
            });
            return;
          }
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            const row = n[idx];

            const newHistoryItem = {
              type: "image",
              url: imageUrl,
              prompt: promptText,
              model: "gemini",
              created_at: Date.now(),
            };

            const syncedRow = syncGeneratedImageForRow(row, imageUrl, {
              source: "generate",
              model: "gemini",
              historyItem: newHistoryItem,
              clearLastFrame: true,
            });

            n[idx] = {
              ...syncedRow,
              isGenerating: false,
              continuity: continuity || syncedRow.continuity,
              openai_generation: openaiGeneration || syncedRow.openai_generation,
              video_prompt: syncedRow.video_prompt || promptText,
            };
            performAutoSave?.(n);
            return n;
          });
        },
        onError: (errorMessage) => {
          delete pollIntervalsRef.current[pollKey];
          toast.error(`生成失败: ${errorMessage}`, 0);
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev; // 返回 prev 而不是 n
            n[idx] = { ...n[idx], isGenerating: false };
            return n;
          });
        },
      });

      pollIntervalsRef.current[pollKey] = stop;

    } catch (e) {
      console.error("提交失败:", e);
      toast.error(`提交失败: ${e.message}`, 0);
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return prev; // 返回 prev 而不是 n
        n[idx] = { ...n[idx], isGenerating: false };
        return n;
      });
    }
  }, [buildRefList, setRows, performAutoSave, clearPollInterval, toast]);

  /**
   * 批量生成4张候选图
   * 修复：1) 改为并发提交（不再 for+await 串行）
   *       2) 传 shotId 给后端持久化，刷新后可恢复
   *       3) 用闭包计数器正确跟踪完成数
   */
  const handleGenerateImageBatch = useCallback(async (
    index,
    rows,
    currentStyle,
    currentRatio,
    buildRefListFn,
    characterOptions,
    sceneOptions,
    propOptions
  ) => {
    const currentRow = rows[index];
    const shotId = currentRow.id;
    const promptText =
      currentRow.image_prompt || currentRow.visual_content || currentRow.action;
    if (!promptText) {
      toast.warning("请输入画面 Prompt");
      return;
    }

    setRows((prev) => {
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx === -1) return prev; // 返回 prev 而不是 n
      n[idx] = { ...n[idx], isGenerating: true, image_candidates: [] };
      return n;
    });

    try {
      const refBuilder = buildRefListFn || buildRefList;
      const characterRefs = refBuilder(
        currentRow.refCharacterIds,
        characterOptions,
        currentRow.refCharacterVariantIndexes
      );
      const sceneRefs = refBuilder(
        currentRow.refSceneIds,
        sceneOptions,
        currentRow.refSceneVariantIndexes
      );
      const propRefs = refBuilder(
        currentRow.refPropIds,
        propOptions,
        currentRow.refPropVariantIndexes
      );

      const directorEnabled = Boolean(currentRow.director_enabled);
      const directorContext = directorEnabled ? buildDirectorContextPayload(currentRow) : null;
      const imageProvider = getCurrentImageProvider();
      const requestedQualityTier = currentRow.image_quality_tier
        || currentRow.imageQualityTier
        || localStorage.getItem("image_quality_tier")
        || "4K";
      assertWanxImageInputLimit([
        ...characterRefs,
        ...sceneRefs,
        ...propRefs,
        currentRow.sketch_base64,
      ], imageProvider);

      const payload = {
        prompt: promptText,
        style: currentStyle,
        aspectRatio: currentRatio,
        characterRefs,
        sceneRefs,
        propRefs,
        continuityMode: null,
        ref_shot_index: null,
        continuity: null,
        openai_generation: null,
        directorEnabled,
        directorContext,
        sketchBase64: currentRow.sketch_base64,
        prevImageBase64: null,
        imageQualityTier: normalizeWanxImageQualityTier(requestedQualityTier, imageProvider),
      };

      const count = 4;
      let completedCount = 0;

      // 并发提交所有任务（Promise.all），不再串行 await
      const submitPromises = [];
      for (let i = 0; i < count; i++) {
        submitPromises.push(
          submitTask("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
            provider: imageProvider,
            styleAnchor: currentStyle,
            shotId: shotId, // 传 shotId，后端持久化任务状态，刷新后可恢复
          }).then(({ taskId }) => ({ taskId, batchIndex: i }))
        );
      }

      // 等待所有提交完成（部分失败不影响其他）
      const results = await Promise.allSettled(submitPromises);

      // 统计提交失败的数量
      let submitFailCount = 0;
      for (const result of results) {
        if (result.status === 'rejected') {
          submitFailCount++;
          completedCount++;
          toast.error(`提交失败: ${result.reason?.message || '未知错误'}`, 0);
        }
      }

      // 如果全部提交失败，直接解锁
      if (submitFailCount >= count) {
        setRows((prev) => {
          const n = [...prev];
          const idx = n.findIndex(s => s.id === shotId);
          if (idx === -1) return n;
          n[idx] = { ...n[idx], isGenerating: false };
          return n;
        });
        return;
      }

      // 为成功提交的任务启动轮询
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const { taskId, batchIndex } = result.value;

        const pollKey = `img_batch_${shotId}_${batchIndex}`;
        clearPollInterval(pollKey);

        const stop = startTaskPolling(taskId, {
          onSuccess: (imageResult) => {
            const imageUrl = getImageUrlFromResult(imageResult);
            const openaiGeneration = getOpenAiGenerationFromResult(imageResult);
            const continuity = getContinuityFromResult(imageResult);
            delete pollIntervalsRef.current[pollKey];
            completedCount++;
            if (!imageUrl) {
              toast.error(`第 ${batchIndex + 1} 张未返回图片地址`, 0);
              setRows((prev) => {
                const n = [...prev];
                const idx = n.findIndex(s => s.id === shotId);
                if (idx === -1) return prev;
                const allDone = completedCount >= count;
                n[idx] = { ...n[idx], isGenerating: !allDone };
                return n;
              });
              return;
            }

            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              const row = n[idx];
              const newHistoryItem = {
                type: "image",
                url: imageUrl,
                prompt: promptText,
                model: "gemini",
                created_at: Date.now(),
              };

              const updatedCandidates = [...(row.image_candidates || []), imageUrl];
              const allDone = completedCount >= count;

              const baseRow = row.generated_image
                  ? row
                  : syncGeneratedImageForRow(row, imageUrl, {
                      source: "batch-candidate",
                      model: "gemini",
                      historyItem: newHistoryItem,
                      clearLastFrame: true,
                    });

              n[idx] = {
                ...baseRow,
                isGenerating: !allDone,
                image_candidates: updatedCandidates,
                continuity: continuity || baseRow.continuity,
                openai_generation: openaiGeneration || baseRow.openai_generation,
                video_prompt: baseRow.video_prompt || promptText,
                history_list: row.generated_image
                  ? [...(row.history_list || []), newHistoryItem].slice(0, 10)
                  : baseRow.history_list,
              };

              if (allDone) performAutoSave?.(n);
              return n;
            });
          },
          onError: (errorMessage) => {
            delete pollIntervalsRef.current[pollKey];
            completedCount++;
            toast.error(`第 ${batchIndex + 1} 张生成失败: ${errorMessage}`, 0);

            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              const allDone = completedCount >= count;
              n[idx] = { ...n[idx], isGenerating: !allDone };
              return n;
            });
          },
        });

        pollIntervalsRef.current[pollKey] = stop;
      }

    } catch (e) {
      console.error("提交失败:", e);
      toast.error(`提交失败: ${e.message}`, 0);
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = { ...n[idx], isGenerating: false };
        return n;
      });
    }
  }, [buildRefList, setRows, performAutoSave, clearPollInterval, toast]);

  /**
   * 生成视频（后端负责轮询，前端只轮询后端状态）
   */
  const handleGenerateVideo = useCallback(async (
    index,
    rows,
    currentRatio,
    mode = "image_to_video",
    currentStyle,
    characterOptions,
    sceneOptions,
    propOptions,
    multiShotConfig = null
  ) => {
    if (!videoEnabledRef.current) return;
    const row = rows[index];
    const shotId = row.id;
    const selectedModelId = getRowVideoProvider(row);
    if (row.isGeneratingVideo) return;

    // 万相允许纯媒体请求；具体模式所需媒体交给统一 validator 校验。
    if (selectedModelId !== "wanx" && !row.video_prompt && !row.image_prompt) {
      toast.warning("需填写描述或生成图片");
      return;
    }

    try {
      // 打光预设注入
      let finalPrompt = row.video_prompt || row.image_prompt;
      if (row.lighting) {
        const { applyLighting } = await import('../components/storyboard/common/LightingPresets.jsx');
        finalPrompt = applyLighting(finalPrompt, row.lighting);
      }
      const durationValue = (row.video_duration || "5s").replace("s", "");

      let startImageUrl = null;
      let endImageUrl = null;

      if (mode === "image_to_video" || (selectedModelId === "wanx"
        && ["first_frame", "first_last_frame"].includes(row.video_wan_mode || "first_frame"))) {
        // 去除 URL 首尾空格
        startImageUrl = row.generated_image ? row.generated_image.trim() : null;
      }

      if (row.video_end_shot_index != null) {
        // 去除 URL 首尾空格
        const endImage = rows[row.video_end_shot_index]?.generated_image;
        endImageUrl = endImage ? endImage.trim() : null;
      }

      // 如果有 multiShotConfig，使用其中的引用配置；否则使用 row 的引用
      let characterRefs, sceneRefs, propRefs;
      if (multiShotConfig) {
        characterRefs = buildRefList(
          multiShotConfig.refCharacterIds,
          characterOptions,
          multiShotConfig.refCharacterVariantIndexes
        );
        sceneRefs = buildRefList(
          multiShotConfig.refSceneIds,
          sceneOptions,
          multiShotConfig.refSceneVariantIndexes
        );
        propRefs = buildRefList(
          multiShotConfig.refPropIds,
          propOptions,
          multiShotConfig.refPropVariantIndexes
        );
      } else {
        characterRefs = buildRefList(row.refCharacterIds, characterOptions, row.refCharacterVariantIndexes);
        sceneRefs = buildRefList(row.refSceneIds, sceneOptions, row.refSceneVariantIndexes);
        propRefs = buildRefList(row.refPropIds, propOptions, row.refPropVariantIndexes);
      }

      const selectedReferenceUrls = getReferenceUrlsFromRefs(characterRefs, sceneRefs, propRefs);
      const wanxMode = selectedModelId === "wanx" ? (row.video_wan_mode || "first_frame") : null;
      const guaManualReferenceUrls = row.video_gua_ref_images || [];
      if (mode === "image_to_video" && !row.generated_image) {
        const guaMode = row.video_gua_mode || "ref2v";
        const isGuaRefOrText = isGuaLikeVideoProvider(selectedModelId) && (guaMode === "ref2v" || guaMode === "t2v");
        const isWanxWithoutFrame = selectedModelId === "wanx"
          && (wanxMode === "text_to_video" || wanxMode === "reference_to_video");
        const isViduRef = selectedModelId === "vidu" && row.video_vidu_use_refs && selectedReferenceUrls.length > 0;
        if (!isGuaRefOrText && !isWanxWithoutFrame && !isViduRef) {
          toast.warning("请先生成图片或使用模型自己的参考图/文生视频模式");
          return;
        }
      }

      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = {
          ...n[idx],
          isGeneratingVideo: true,
          isEditingVideoPrompt: false,
          video_task_error: null,
        };
        performAutoSave?.(n);
        return n;
      });

      const payload = {
        provider: selectedModelId,
        prompt: finalPrompt,
        mode,
        startImageUrl,
        endImageUrl,
        refCharacterImages: characterRefs,
        refSceneImages: sceneRefs,
        refPropImages: propRefs,
        duration: durationValue,
        aspectRatio: currentRatio,
      };

      // 万相3.0 All-in-One：使用类型化 media，和 Seedance/Gua 素材协议完全隔离。
      if (selectedModelId === "wanx") {
        const explicitMedia = Array.isArray(row.video_wan_media)
          ? row.video_wan_media.filter((item) => item?.type && item?.url)
          : [];
        let requestedMedia = [];

        if (wanxMode === "text_to_video") {
          requestedMedia = [];
        } else if (wanxMode === "first_frame") {
          if (!startImageUrl) {
            throw new Error("万相3.0首帧模式需要先生成当前分镜图片");
          }
          requestedMedia = [{ type: WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, url: startImageUrl }];
        } else if (wanxMode === "first_last_frame") {
          if (!startImageUrl || !endImageUrl) {
            throw new Error("万相3.0首尾帧模式需要同时指定起始帧和结束帧");
          }
          requestedMedia = [
            { type: WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, url: startImageUrl },
            { type: WANX_VIDEO_MEDIA_TYPES.LAST_FRAME, url: endImageUrl },
          ];
        } else {
          const combined = [
            ...explicitMedia,
            ...selectedReferenceUrls.map((url) => ({
              type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
              url,
            })),
          ];
          requestedMedia = Array.from(new Map(
            combined.map((item) => [`${item.type}:${item.url}`, item]),
          ).values());
        }

        const capabilityMode = WANX_STORYBOARD_MODE[wanxMode] || "reference";
        const requestPrompt = capabilityMode === "reference"
          ? resolveWanxVideoMediaMarkers(finalPrompt || "", requestedMedia)
          : stripRefMarkers(finalPrompt || "");
        const wanxRequest = validateWanxVideoRequest({
          model: row.video_sub_model || row.videoSubModel || row.video_model,
          prompt: requestPrompt,
          media: requestedMedia,
          mode: capabilityMode,
          duration: row.video_wan_duration ?? 5,
          resolution: row.video_wan_resolution || "1080P",
          ratio: row.video_wan_ratio || "adaptive",
          seed: row.video_wan_seed,
        });

        payload.provider = "wanx";
        payload.model = wanxRequest.model;
        payload.prompt = wanxRequest.prompt;
        payload.mode = wanxMode;
        payload.generationMode = wanxMode;
        payload.duration = wanxRequest.duration;
        payload.resolution = wanxRequest.resolution;
        payload.aspectRatio = wanxRequest.ratio;
        payload.ratio = wanxRequest.ratio;
        payload.audio = row.video_wan_audio ?? true;
        payload.sound = (row.video_wan_audio ?? true) ? "on" : "off";
        payload.watermark = row.video_wan_watermark ?? false;
        payload.promptExtend = row.video_wan_prompt_extend !== false;
        if (wanxRequest.seed != null) payload.seed = wanxRequest.seed;
        payload.wanxMedia = wanxRequest.media.map((item) => ({
          type: item.type,
          url: item.url,
          ...(item.duration != null ? { durationSeconds: item.duration } : {}),
        }));
        // 避免兼容字段被后端再次收集，造成同一素材重复或首帧/参考模式冲突。
        payload.startImageUrl = null;
        payload.endImageUrl = null;
        payload.refCharacterImages = [];
        payload.refSceneImages = [];
        payload.refPropImages = [];
      }

      // Vidu-specific params
      if (selectedModelId === "vidu") {
        // 默认子模型和分辨率
        payload.model = row.video_sub_model || "viduq3-turbo";
        payload.resolution = row.video_resolution || "720p";
        // duration for vidu is an integer (seconds), override the string value above
        if (row.video_vidu_duration != null) payload.duration = String(row.video_vidu_duration);
        else payload.duration = "5"; // 默认 5 秒
        if (row.video_audio != null) payload.audio = row.video_audio;
        if (row.video_audio_type) payload.audioType = row.video_audio_type;
        if (row.video_bgm != null) payload.bgm = row.video_bgm;
        if (row.video_off_peak != null) payload.offPeak = row.video_off_peak;
        if (row.video_movement_amplitude) payload.movementAmplitude = row.video_movement_amplitude;
        if (row.video_voice_id) payload.voiceId = row.video_voice_id;
        if (row.video_seed != null) payload.seed = row.video_seed;

        // Vidu 默认不传参考图（走首帧图生视频），除非用户主动开启
        if (!row.video_vidu_use_refs) {
          payload.refCharacterImages = [];
          payload.refSceneImages = [];
          payload.refPropImages = [];
        }
      }

      // WangsuSora-specific params
      if (selectedModelId === "wangsuSora") {
        if (row.video_sub_model) payload.model = row.video_sub_model;
        if (row.video_resolution) payload.resolution = row.video_resolution;
        // duration for wangsuSora is an integer (seconds), override the string value above
        if (row.video_wangsu_duration != null) payload.duration = String(row.video_wangsu_duration);
      }

      // HappyHorse-specific params
      if (selectedModelId === "happyhorse") {
        payload.resolution = row.video_resolution || "1080P";
        // 根据生成模式自动选子模型
        if (mode === "reference_to_video" || (selectedReferenceUrls.length > 0 && !startImageUrl)) {
          payload.model = "happyhorse-r2v";
          // 参考图 URL 列表
          payload.referenceUrls = selectedReferenceUrls;
          // prompt 里自动加 characterN 标记
          if (!finalPrompt.includes('character') && selectedReferenceUrls.length > 0) {
            const charLabels = selectedReferenceUrls.map((_, i) => `character${i + 1}`).join('、');
            payload.prompt = `画面中出现${charLabels}，${finalPrompt}`;
          }
          // 参考模式不需要首帧
          payload.startImageUrl = null;
          payload.refCharacterImages = [];
          payload.refSceneImages = [];
          payload.refPropImages = [];
        } else if (startImageUrl) {
          payload.model = "happyhorse-i2v";
        } else {
          payload.model = "happyhorse-t2v";
        }
      }

      // GuaVideo-specific params
      if (isGuaLikeVideoProvider(selectedModelId)) {
        const projectIdForVideo = currentProjectIdFromPath();
        const projectVideoProvider = isDomesticOfficialSeedance(selectedModelId) ? selectedModelId : getProjectVideoProvider(projectIdForVideo, "guavideo");
        if (!isSeedanceVideoProvider(projectVideoProvider)) {
          throw new Error("当前项目的视频服务配置无效，请刷新项目配置后重试");
        }
        const overseasGuaVideo = isOverseasVideoProvider(projectVideoProvider);
        const guaMode = row.video_gua_mode || "ref2v";
        const currentStoryboardImageRefs = guaMode === "ref2v" && isSendableMediaUrl(row.generated_image)
          ? [row.generated_image.trim()]
          : [];
        const guaReferenceItems = [];
        const guaReferenceSeen = new Set();
        const addGuaReferenceItem = (item) => {
          const original = typeof item === "string" ? item.trim() : getGuaRefUrl(item).trim();
          const url = overseasGuaVideo ? getDreaminaProxyOriginalUrl(item) : original;
          if (overseasGuaVideo && !url && (/^asset:\/\//i.test(original)
            || item?.assetId || item?.engGuaVideoAssetId || item?.guaOminiAssetId || item?.dreaminaProxyAssetId)) {
            requireDreaminaProxyOriginalUrl(item);
          }
          if (!isSendableMediaUrl(url) || guaReferenceSeen.has(url) || guaReferenceItems.length >= 9) return;
          guaReferenceSeen.add(url);
          guaReferenceItems.push(typeof item === "string" ? { url } : { ...item, url });
        };
        const guaManualReferenceItems = (guaManualReferenceUrls || []).map((url) => (
          (row.video_gua_ref_items || []).find((item) => getGuaRefUrl(item) === url) || { url }
        ));
        guaManualReferenceItems.forEach(addGuaReferenceItem);
        currentStoryboardImageRefs.forEach(addGuaReferenceItem);
        if (guaMode === "ref2v") {
          [...characterRefs, ...sceneRefs, ...propRefs].forEach(addGuaReferenceItem);
        }
        const guaReferenceImages = guaReferenceItems.map((item) => item.url);
        const guaContents = [];

        // 文本 prompt（GuaVideo ref2v 用语义化替换，其他模式去掉标记）
        if (finalPrompt) {
          let cleanPrompt = (guaMode === 'ref2v')
            ? resolveGuaVideoRefMarkers(finalPrompt, {
              images: guaReferenceImages,
              videos: row.video_gua_ref_videos || [],
              audios: [],
              audioOffset: 0,
            })
            : stripRefMarkers(finalPrompt);
          // 去字幕：在提示词前注入指令
          if (row.video_gua_no_subtitle) {
            cleanPrompt = `禁止BGM，禁止字幕，如果出现字幕则字幕透明度设置成100%。${cleanPrompt}`;
          }
          if (guaMode === "ref2v") {
            cleanPrompt = applyGuaVoiceBindingPrompt(cleanPrompt, [], { overseas: overseasGuaVideo });
          }
          guaContents.push({ type: "text", text: cleanPrompt });
        } else if (row.video_gua_no_subtitle) {
          // 没有用户提示词但开了去字幕，也要注入
          const cleanPrompt = applyGuaVoiceBindingPrompt("禁止BGM，禁止字幕，如果出现字幕则字幕透明度设置成100%。", [], { overseas: overseasGuaVideo });
          guaContents.push({ type: "text", text: cleanPrompt });
        }

        if (guaMode === "i2v") {
          // 首帧
          if (startImageUrl) {
            guaContents.push({ type: "image_url", url: startImageUrl, role: "first_frame" });
          }
          // 尾帧
          if (endImageUrl) {
            guaContents.push({ type: "image_url", url: endImageUrl, role: "last_frame" });
          }
        } else if (guaMode === "ref2v") {
          // 参考图：GuaVideo 独立参考图 + 当前分镜手动选择的角色/场景/道具参考图
          guaReferenceItems.forEach(item => {
            const content = { type: "image_url", url: item.url, role: "reference_image" };
            const assetMeta = getEffectiveAssetMeta(item, projectVideoProvider);
            const assetId = String(assetMeta.assetId || "").replace(/^asset:\/\//i, "");
            if (assetId && assetMeta.status === "Active" && projectVideoProvider === "guavideo") {
              content.assetId = assetId;
            }
            guaContents.push(content);
          });
          // 参考视频
          (row.video_gua_ref_videos || []).forEach(url => {
            guaContents.push({ type: "video_url", url, role: "reference_video" });
          });
        }
        // t2v: 只有 text，不加媒体

        payload.provider = projectVideoProvider;
        const useFastDreamina = String(row.video_sub_model || "").toLowerCase().includes("fast");
        payload.model = overseasGuaVideo
          ? (row.video_sub_model?.startsWith?.("dreamina-seedance")
            ? row.video_sub_model
            : (useFastDreamina ? "dreamina-seedance-2-0-fast" : "dreamina-seedance-2-0"))
          : row.video_sub_model || "doubao-seedance-2-0-260128";
        const preparedGuaContents = await preflightVideoAssetContents({
          provider: projectVideoProvider,
          projectId: projectIdForVideo,
          contents: guaContents,
          context: {
            shotNumber: row.shot_number || row.shotNumber || row.id,
            name: `分镜 ${row.shot_number || row.shotNumber || row.id || "参考图"}`,
            source: "storyboard",
            sourceDetail: "storyboard_list_video_preflight",
          },
          shouldContinue: () => videoEnabledRef.current,
        });
        payload.prompt = preparedGuaContents.find((item) => item.type === "text")?.text || payload.prompt;
        payload.guaVideoContents = preparedGuaContents;
        payload.guaVideoMode = guaMode;
        payload.generateAudio = row.video_gua_audio ?? true;
        payload.duration = String(row.video_gua_duration ?? 5);
        payload.resolution = normalizeVideoResolutionForProvider(
          projectVideoProvider,
          row.video_gua_resolution,
          "480p",
        );
        payload.ratio = row.video_gua_ratio || currentRatio || "9:16";
        payload.guaWebSearch = guaMode === "t2v" && (row.video_gua_web_search ?? false);
        payload.projectId = projectIdForVideo || undefined;
        // ref2v/t2v 不需要 startImageUrl
        if (guaMode !== "i2v") {
          payload.startImageUrl = null;
          payload.endImageUrl = null;
          payload.mode = "text_to_video";
        }
      }

      // 带上 shotId，后端负责持久化并轮询
      const headers = {
        "Content-Type": "application/json",
      };
      if (row.id) headers["X-Shot-Id"] = String(row.id);

      // 万相走统一提交层；其他厂商继续走原请求路径，避免改变既有提交行为。
      const usesUnifiedSubmission = payload.provider === "wanx" || isDomesticOfficialSeedance(payload.provider);
      const resp = usesUnifiedSubmission
        ? await submitVideoTask({ ...payload, shotId: row.id })
        : await authenticatedFetch(`${API_HOST}/api/video/generate`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

      if (isSeedanceVideoProvider(payload.provider)) {
        const rawResponseProvider = String(resp?.provider || resp?.videoProvider || "").trim();
        if (rawResponseProvider && normalizeProjectVideoProvider(rawResponseProvider) !== payload.provider) {
          throw new Error("视频服务返回与当前项目不匹配，已拒绝接收任务");
        }
      }
      if (payload.provider === "wanx") {
        const rawResponseProvider = String(resp?.provider || resp?.videoProvider || "").trim();
        if (rawResponseProvider && normalizeVideoProvider(rawResponseProvider) !== "wanx") {
          throw new Error("视频服务返回的厂商与万相 3.0 不匹配，已拒绝接收任务");
        }
      }

      const providerTaskId = usesUnifiedSubmission ? resp.taskId : resp.task_id;

      // 更新本地状态为 polling
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = {
          ...n[idx],
          isGeneratingVideo: videoEnabledRef.current,
          video_task_id: providerTaskId,
          video_task_status: "polling",
          video_provider: payload.provider || selectedModelId,
          video_task_error: null,
          ...(selectedModelId === "wanx" ? {
            video_model: "wanx",
            video_sub_model: payload.model,
          } : {}),
        };
        return n;
      });

      // 用户在提交请求期间隐藏了视频区：保留后端任务信息，但不启动前端轮询。
      if (!videoEnabledRef.current) {
        return { taskId: providerTaskId, suspended: true };
      }

      // 前端轮询后端状态（不直接查服务商）
      const pollKey = `vid_${shotId}`;
      clearPollInterval(pollKey);

      const stop = startVideoStatusPolling(shotId, providerTaskId, payload.provider || selectedModelId, {
        onSuccess: (videoUrl, lastFrameImage) => {
          delete pollIntervalsRef.current[pollKey];
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            const currentRow = n[idx];

            // 构建完整的 history item，包含费用计算所需参数
            const historyItem = {
              type: "video",
              url: videoUrl,
              prompt: finalPrompt,
              model: selectedModelId,
              created_at: Date.now(),
            };

            // 添加视频生成参数（用于费用计算）
            if (selectedModelId === "vidu") {
              historyItem.sub_model = currentRow.video_sub_model || "viduq3-turbo";
              historyItem.duration = parseInt(currentRow.video_vidu_duration || currentRow.video_duration || "5");
              historyItem.resolution = currentRow.video_resolution || "720p";
            } else if (selectedModelId === "wangsuSora") {
              historyItem.sub_model = currentRow.video_sub_model || "sora-2";
              historyItem.duration = parseInt(currentRow.video_wangsu_duration || currentRow.video_duration || "4");
              historyItem.resolution = currentRow.video_resolution || "1280x720";
            } else if (selectedModelId === "minimax") {
              historyItem.sub_model = currentRow.video_sub_model || "MiniMax-Hailuo-2.3";
              historyItem.duration = parseInt(currentRow.video_duration || "6");
              historyItem.resolution = currentRow.video_resolution || "768p";
            } else if (isGuaLikeVideoProvider(selectedModelId)) {
              historyItem.sub_model = currentRow.video_sub_model || "guavideo";
              historyItem.duration = parseInt(currentRow.video_gua_duration || currentRow.video_duration || "5");
              historyItem.resolution = normalizeVideoResolutionForProvider(
                payload.provider || selectedModelId,
                currentRow.video_gua_resolution,
                "480p",
              );
              historyItem.mode = currentRow.video_gua_mode || "ref2v";
              historyItem.sound = (currentRow.video_gua_audio ?? true) ? "on" : "off";
            } else if (selectedModelId === "wanx") {
              historyItem.provider = "wanx";
              historyItem.sub_model = payload.model;
              historyItem.duration = payload.duration;
              historyItem.resolution = payload.resolution;
              historyItem.mode = wanxMode;
              historyItem.audio = payload.audio !== false;
              historyItem.sound = payload.audio === false ? "off" : "on";
              historyItem.ratio = payload.ratio;
              historyItem.watermark = payload.watermark === true;
              historyItem.promptExtend = payload.promptExtend !== false;
              historyItem.seed = payload.seed ?? null;
              historyItem.wanxMedia = payload.wanxMedia.map((item) => ({ ...item }));
            } else {
              // 其他模型：通用处理
              historyItem.sub_model = currentRow.video_sub_model;
              historyItem.duration = parseInt(currentRow.video_duration || "5");
            }

            n[idx] = {
              ...n[idx],
              isGeneratingVideo: false,
              generated_video: videoUrl,
              last_frame_image: lastFrameImage || null,
              video_task_status: "success",
              video_task_error: null,
              history_list: [...(currentRow.history_list || []), historyItem].slice(0, 10),
            };
            performAutoSave?.(n);
            return n;
          });
        },
        onError: (errorMessage) => {
          delete pollIntervalsRef.current[pollKey];
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            n[idx] = {
              ...n[idx],
              isGeneratingVideo: false,
              video_task_status: "failed",
              video_task_error: errorMessage || "视频生成失败",
            };
            return n;
          });
          toast.error(`视频生成失败: ${errorMessage}`, 0);
        },
      });

      pollIntervalsRef.current[pollKey] = stop;

    } catch (e) {
      toast.error("提交失败: " + e.message, 0);
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = {
          ...n[idx],
          isGeneratingVideo: false,
          video_task_status: "failed",
          video_task_error: e.message || "视频提交失败",
        };
        return n;
      });
    }
  }, [buildRefList, setRows, performAutoSave, clearPollInterval, toast]);

  /**
   * 自动生成视频提示词（异步提交 + 轮询模式）
   */
  const handleAutoVideoPrompt = useCallback(async (
    index,
    rows,
    currentStyle
  ) => {
    if (!videoEnabledRef.current) return;
    const currentRow = rows[index];
    const shotId = currentRow.id;

    // 设置生成中状态
    setRows((prev) => {
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx === -1) return n;
      n[idx] = { ...n[idx], isGeneratingPrompt: true };
      return n;
    });

    try {
      const { taskId } = await submitTask("GENERATE_VIDEO_PROMPT", {
        shot: currentRow.shot,
        action: currentRow.action,
        audio: currentRow.audio,
        visual_content: currentRow.visual_content,
      }, {
        styleAnchor: currentStyle,
      });

      const pollKey = `prompt_${shotId}`;
      clearPollInterval(pollKey);

      const stop = startTaskPolling(taskId, {
        onSuccess: (result) => {
          delete pollIntervalsRef.current[pollKey];
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            n[idx] = { ...n[idx], isGeneratingPrompt: false, video_prompt: result };
            performAutoSave?.(n);
            return n;
          });
        },
        onError: (errorMessage) => {
          delete pollIntervalsRef.current[pollKey];
          toast.error(`生成视频提示词失败: ${errorMessage}`, 0);
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            n[idx] = { ...n[idx], isGeneratingPrompt: false };
            return n;
          });
        },
      });

      pollIntervalsRef.current[pollKey] = stop;

    } catch (e) {
      toast.error("提交视频提示词任务失败: " + e.message, 0);
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = { ...n[idx], isGeneratingPrompt: false };
        return n;
      });
    }
  }, [setRows, performAutoSave, clearPollInterval]);

  /**
   * 局部重绘/图片修复（异步提交 + 轮询模式）
   */
  const handleRedraw = useCallback(async (
    index,
    prompt,
    rows,
    currentRatio
  ) => {
    const currentRow = rows[index];
    const shotId = currentRow.id;
    if (!currentRow.generated_image) {
      toast.warning("请先生成图片");
      return;
    }

    // 获取 base64 图片
    let base64 = currentRow.generated_image;
    if (base64.startsWith("http")) {
      base64 = await urlToBase64(base64);
    }

    if (!base64) {
      toast.warning("图片加载失败");
      return;
    }

    // 设置生成中状态
    setRows((prev) => {
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx === -1) return n;
      n[idx] = { ...n[idx], isGenerating: true };
      return n;
    });

    try {
      const imageProvider = getCurrentImageProvider();
      const { taskId } = await submitTask("INPAINT_IMAGE", {
        baseImage: base64,
        prompt,
        aspectRatio: currentRatio,
        imageQualityTier: normalizeWanxImageQualityTier(
          currentRow.image_quality_tier
            || currentRow.imageQualityTier
            || localStorage.getItem("image_quality_tier")
            || "4K",
          imageProvider,
        ),
      }, { provider: imageProvider });

      const pollKey = `img_${shotId}`;
      clearPollInterval(pollKey);

      const stop = startTaskPolling(taskId, {
        onSuccess: (result) => {
          const redrawUrl = getImageUrlFromResult(result);
          delete pollIntervalsRef.current[pollKey];
          if (!redrawUrl) {
            toast.error("重绘成功但未返回图片地址", 0);
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              n[idx] = { ...n[idx], isGenerating: false };
              return n;
            });
            return;
          }
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            const row = n[idx];

            const newHistoryItem = {
              type: "image",
              url: redrawUrl,
              prompt: `重绘: ${prompt}`,
              model: "gemini",
              created_at: Date.now(),
            };

            const syncedRow = syncGeneratedImageForRow(row, redrawUrl, {
              source: "redraw",
              model: "gemini",
              historyItem: newHistoryItem,
              clearLastFrame: true,
            });

            n[idx] = {
              ...syncedRow,
              isGenerating: false,
            };
            performAutoSave?.(n);
            return n;
          });
        },
        onError: (errorMessage) => {
          delete pollIntervalsRef.current[pollKey];
          toast.error("重绘失败: " + errorMessage, 0);
          setRows((prev) => {
            const n = [...prev];
            const idx = n.findIndex(s => s.id === shotId);
            if (idx === -1) return prev;
            n[idx] = { ...n[idx], isGenerating: false };
            return n;
          });
        },
      });

      pollIntervalsRef.current[pollKey] = stop;

    } catch (e) {
      toast.error("提交重绘任务失败: " + e.message, 0);
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = { ...n[idx], isGenerating: false };
        return n;
      });
    }
  }, [setRows, performAutoSave, clearPollInterval, toast]);

  /**
   * 停止视频生成
   * @param {number} index - 行索引
   */
  const stopVideoGeneration = useCallback((index) => {
    setRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const shotId = row.id;

      // 用 shotId 作为 pollKey（与启动时一致）
      const pollKey = `vid_${shotId}`;
      clearPollInterval(pollKey);

      // 调用后端取消 API，清理数据库中的 polling 状态
      if (shotId) {
        authenticatedFetch(`${API_HOST}/api/video/cancel?shotId=${shotId}`, {
          method: 'POST'
        }).catch(console.error);
      }

      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx !== -1) {
        n[idx] = {
          ...n[idx],
          isGeneratingVideo: false,
          video_task_status: "failed"
        };
      }
      return n;
    });
  }, [clearPollInterval, setRows]);

  /**
   * 停止图片生成
   * @param {number} index - 行索引
   */
  const stopImageGeneration = useCallback(async (index) => {
    setRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const shotId = row.id;

      // 用 shotId 作为 pollKey（与启动时一致）
      clearPollInterval(`img_${shotId}`);

      if (shotId) {
        authenticatedFetch(`${API_HOST}/api/task/image-cancel?shotId=${shotId}`, {
          method: 'POST'
        }).catch(console.error);
      }

      // 重置生成状态
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx !== -1) {
        n[idx] = { ...n[idx], isGenerating: false };
      }
      return n;
    });
  }, [clearPollInterval, setRows]);

  /**
   * 批量生成 2x2 宫格图（跨分镜连戏）
   * 提交单个 BATCH_GENERATE_GRIDS 任务，后端顺序处理所有 shot
   */
  const handleBatchGenerateGrids = useCallback(async (
    rows,
    currentStyle,
    currentRatio,
    projectId,
    episodeId
  ) => {
    if (!rows || rows.length === 0) {
      throw new Error("没有分镜数据");
    }

    // 提交前记录当前 rows 的 id 列表，用于回调时按 id 定位
    const shotIds = rows.map(r => r.id);

    // 1. 构建 shots payload（传递完整分镜数据供后端生成视频提示词）
    const shots = rows.map((row) => ({
      image_prompt: row.image_prompt || "",
      action: row.action || "",
      content: row.content || "",
      audio_desc: row.audio_desc || "",
      visual_content: row.visual_content || "",
      shot: row.shot || "",
      shot_number: row.shot_number || "",
      time: row.time || "3s",
      refCharacterIds: row.refCharacterIds || [],
      refSceneIds: row.refSceneIds || [],
      refPropIds: row.refPropIds || [],
    }));
    const imageProvider = getCurrentImageProvider();
    shots.forEach((shot, shotIndex) => {
      try {
        assertWanxImageInputLimit(
          [
            ...shot.refCharacterIds,
            ...shot.refSceneIds,
            ...shot.refPropIds,
          ],
          imageProvider,
        );
      } catch (error) {
        throw new Error(`分镜 ${shotIndex + 1}: ${error.message}`);
      }
    });

    // 2. 设置所有行为生成中
    setRows((prev) => prev.map((r) => ({ ...r, isGenerating: true })));

    try {
      // 3. 提交单个批量任务
      const { taskId } = await submitTask("BATCH_GENERATE_GRIDS", {
        shots,
        projectId,
        episodeId,
        aspectRatio: currentRatio,
        imageQualityTier: normalizeWanxImageQualityTier(
          localStorage.getItem("image_quality_tier") || "4K",
          imageProvider,
        ),
      }, {
        provider: imageProvider,
        styleAnchor: currentStyle,
      });

      // 4. 轮询（间隔 5s，批量任务耗时较长）
      return new Promise((resolve, reject) => {
        const pollKey = "batch_grids";
        clearPollInterval(pollKey);

        const stop = startTaskPolling(taskId, {
          interval: 5000,
          onSuccess: (result) => {
            delete pollIntervalsRef.current[pollKey];
            const gridImages = result.gridImages || [];
            const imagePrompts = result.imagePrompts || [];
            const videoPrompts = result.videoPrompts || [];
            const resolvedRefs = result.resolvedRefs || [];

            // 批量更新每行: 用 shotIds 按 id 定位
            setRows((prev) => {
              const n = [...prev];
              gridImages.forEach((url, i) => {
                const targetShotId = shotIds[i];
                const rowIdx = n.findIndex(s => s.id === targetShotId);
                if (rowIdx === -1) return;
                const newHistoryItem = {
                  type: "image",
                  url,
                  prompt: imagePrompts[i] || "批量宫格生成",
                  model: "gemini",
                  created_at: Date.now(),
                };
                const refs = resolvedRefs[i] || {};
                const syncedRow = syncGeneratedImageForRow(n[rowIdx], url, {
                  source: "batch-grid",
                  model: "gemini",
                  historyItem: newHistoryItem,
                  clearLastFrame: true,
                });
                n[rowIdx] = {
                  ...syncedRow,
                  isGenerating: false,
                  image_prompt: imagePrompts[i] || syncedRow.image_prompt,
                  video_prompt: videoPrompts[i] || syncedRow.video_prompt,
                  refCharacterIds: refs.refCharacterIds || syncedRow.refCharacterIds,
                  refSceneIds: refs.refSceneIds || syncedRow.refSceneIds,
                  refPropIds: refs.refPropIds || syncedRow.refPropIds,
                };
              });
              // 清除剩余行的 isGenerating
              n.forEach((row, idx) => {
                if (row.isGenerating) {
                  n[idx] = { ...n[idx], isGenerating: false };
                }
              });
              performAutoSave?.(n);
              return n;
            });
            resolve(result);
          },
          onError: (errorMessage) => {
            delete pollIntervalsRef.current[pollKey];
            // 清除所有行的 isGenerating
            setRows((prev) => prev.map((r) => ({ ...r, isGenerating: false })));
            reject(new Error(errorMessage));
          },
        });

        pollIntervalsRef.current[pollKey] = stop;
      });
    } catch (e) {
      // 提交失败，清除状态
      setRows((prev) => prev.map((r) => ({ ...r, isGenerating: false })));
      throw e;
    }
  }, [setRows, performAutoSave, clearPollInterval]);

  /**
   * 批量生成首尾帧图片（支持部分失败）
   * 提交单个 BATCH_GENERATE_FIRST_LAST_FRAMES 任务
   */
  const handleBatchGenerateFirstLastFrames = useCallback(async (
    rows,
    currentStyle,
    currentRatio,
    projectId,
    episodeId
  ) => {
    if (!rows || rows.length === 0) {
      throw new Error("没有分镜数据");
    }

    // 提取所有分镜的 ID
    const shotIds = rows.map((row) => row.id).filter(id => id != null);
    if (shotIds.length === 0) {
      throw new Error("没有有效的分镜 ID");
    }

    // 设置所有行为生成中
    setRows((prev) => prev.map((r) => ({ ...r, isGenerating: true })));

    try {
      const imageProvider = getCurrentImageProvider();
      // 提交批量任务
      const { taskId } = await submitTask("BATCH_GENERATE_FIRST_LAST_FRAMES", {
        shotIds,
        aspectRatio: currentRatio,
        imageQualityTier: normalizeWanxImageQualityTier(
          localStorage.getItem("image_quality_tier") || "4K",
          imageProvider,
        ),
      }, {
        provider: imageProvider,
        styleAnchor: currentStyle,
      });

      // 轮询（间隔 5s）
      return new Promise((resolve, reject) => {
        const pollKey = "batch_first_last_frames";
        clearPollInterval(pollKey);

        const stop = startTaskPolling(taskId, {
          interval: 5000,
          onSuccess: (result) => {
            delete pollIntervalsRef.current[pollKey];
            const firstFrames = result.firstFrames || [];
            const lastFrames = result.lastFrames || [];
            const successIds = result.successIds || [];
            const failedIds = result.failedIds || [];
            const errorMessages = result.errorMessages || [];

            // 批量更新成功的分镜的首尾帧图片
            setRows((prev) => {
              const n = [...prev];
              n.forEach((row, idx) => {
                if (row.id && successIds.includes(row.id)) {
                  // 找到对应的首尾帧
                  const successIndex = successIds.indexOf(row.id);
                  if (successIndex !== -1) {
                    n[idx] = {
                      ...n[idx],
                      isGenerating: false,
                      first_frame_image: firstFrames[successIndex],
                      last_frame_image: lastFrames[successIndex],
                      firstFrameImage: firstFrames[successIndex],
                      lastFrameImage: lastFrames[successIndex],
                    };
                  }
                } else {
                  // 清除生成中状态
                  n[idx] = { ...n[idx], isGenerating: false };
                }
              });
              performAutoSave?.(n);
              return n;
            });

            // 构建结果信息
            const resultInfo = {
              ...result,
              successCount: successIds.length,
              failedCount: failedIds.length,
              errorMessages,
            };

            resolve(resultInfo);
          },
          onError: (errorMessage) => {
            delete pollIntervalsRef.current[pollKey];
            // 清除所有行的 isGenerating
            setRows((prev) => prev.map((r) => ({ ...r, isGenerating: false })));
            reject(new Error(errorMessage));
          },
        });

        pollIntervalsRef.current[pollKey] = stop;
      });
    } catch (e) {
      // 提交失败，清除状态
      setRows((prev) => prev.map((r) => ({ ...r, isGenerating: false })));
      throw e;
    }
  }, [setRows, performAutoSave, clearPollInterval]);

  /**
   * 批量生成序列视频（首尾帧模式）
   * 使用相邻分镜的图片生成视频：分镜i的图 + 分镜i+1的图
   * 自动生成包含所有对话的视频提示词
   */
  const handleBatchGenerateSequenceVideos = useCallback(async (
    rows,
    currentRatio,
    provider = "vidu",
    model = "viduq3-pro",
    shotDurations = {}, // 改为接收每个分镜的时长映射 { shotId: duration }
    resolution,
  ) => {
    if (!videoEnabledRef.current) {
      throw new Error("视频区域已隐藏，请先开启视频栏");
    }
    if (!rows || rows.length === 0) {
      throw new Error("没有分镜数据");
    }

    // 过滤出有图片的分镜。首尾帧工作流可能只持久化 first_frame_image，
    // 不能仅依赖普通生图字段 generated_image。
    const shotsWithImage = rows.filter((row) => getSequenceShotImage(row));
    if (shotsWithImage.length < 2) {
      throw new Error("至少需要2个分镜有图片才能生成首尾帧视频");
    }

    const projectIdForSequence = Number(projectId || currentProjectIdFromPath());
    const runtime = resolveSequenceVideoRuntime({
      projectId: projectIdForSequence,
      provider,
      model,
      resolution,
    });
    const sequenceProvider = runtime.provider;
    const sequenceModel = runtime.model;
    if (!sequenceProvider || !sequenceModel) {
      throw new Error("项目视频厂商配置尚未加载，请刷新项目后重试");
    }
    const usesWanxSequence = sequenceProvider === "wanx";

    // 设置所有行为生成中
    setRows((prev) => prev.map((r) => ({ ...r, isGeneratingVideo: true })));

    try {
      // 生成 N-1 个视频（相邻分镜组合）
      const videoTasks = [];

      for (let i = 0; i < shotsWithImage.length - 1; i++) {
        const currentShot = shotsWithImage[i];
        const nextShot = shotsWithImage[i + 1];

        // 构建视频提示词，包含所有对话和旁白
        const videoPrompt = buildSequenceVideoPrompt(currentShot, nextShot, i, shotsWithImage.length);

        // 获取当前分镜的时长设置，默认使用分镜的建议时长或 5s
        const requestedDuration = shotDurations[currentShot.id]
          ?? currentShot.time
          ?? 5;
        const shotDuration = normalizeSequenceVideoDuration(
          sequenceProvider,
          requestedDuration,
          5,
        );

        videoTasks.push({
          shotId: currentShot.id,
          startImageUrl: getSequenceShotImage(currentShot),
          endImageUrl: getSequenceShotImage(nextShot),
          prompt: videoPrompt,
          duration: String(shotDuration), // 使用单独设置的时长
        });
      }

      // 并发生成所有视频
      const videoFutures = videoTasks.map(async (task, idx) => {
        const wanxRequest = usesWanxSequence
          ? validateWanxVideoRequest({
            model: sequenceModel,
            prompt: task.prompt,
            media: [
              { type: WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, url: task.startImageUrl },
              { type: WANX_VIDEO_MEDIA_TYPES.LAST_FRAME, url: task.endImageUrl },
            ],
            mode: "start-end",
            duration: task.duration,
            resolution: runtime.resolution,
            ratio: currentRatio,
          })
          : null;
        const taskPayload = {
          shotId: task.shotId,
          provider: sequenceProvider,
          model: wanxRequest?.model || sequenceModel,
          prompt: task.prompt,
          mode: usesWanxSequence ? "first_last_frame" : "image_to_video",
          startImageUrl: task.startImageUrl,
          endImageUrl: task.endImageUrl,
          duration: wanxRequest?.duration ?? task.duration, // 使用任务的单独时长
          aspectRatio: wanxRequest?.ratio || currentRatio,
          resolution: wanxRequest?.resolution || runtime.resolution,
          movementAmplitude: "auto",
        };
        if (wanxRequest) {
          taskPayload.startImageUrl = null;
          taskPayload.endImageUrl = null;
          taskPayload.generationMode = "first_last_frame";
          taskPayload.ratio = wanxRequest.ratio;
          taskPayload.audio = true;
          taskPayload.sound = "on";
          taskPayload.watermark = false;
          taskPayload.promptExtend = true;
          taskPayload.wanxMedia = wanxRequest.media.map((item) => ({
            type: item.type,
            url: item.url,
          }));
          taskPayload.referenceUrls = [];
          taskPayload.refCharacterImages = [];
          taskPayload.refSceneImages = [];
          taskPayload.refPropImages = [];
        }

        const { taskId, provider: returnedProvider } = await submitVideoTask(taskPayload);
        const effectiveProvider = returnedProvider || sequenceProvider;
        setRows((prev) => prev.map((row) => row.id === task.shotId ? {
          ...row,
          isGeneratingVideo: videoEnabledRef.current,
          video_task_id: taskId,
          video_task_status: "polling",
          video_provider: effectiveProvider,
          video_prompt: task.prompt,
          video_task_error: null,
          ...(wanxRequest ? {
            video_model: "wanx",
            video_sub_model: wanxRequest.model,
            video_wan_mode: "first_last_frame",
            video_wan_duration: wanxRequest.duration,
            video_wan_resolution: wanxRequest.resolution,
            video_wan_ratio: wanxRequest.ratio,
            video_wan_audio: true,
          } : {}),
        } : row));

        if (!videoEnabledRef.current) return { taskId, suspended: true };

        return new Promise((resolve, reject) => {
          const pollKey = `seq_vid_${idx}`;
          clearPollInterval(pollKey);
          const stop = startVideoPolling(effectiveProvider, taskId, {
            interval: 5000,
            projectId: projectIdForSequence,
            episodeId,
            prompt: task.prompt,
            model: taskPayload.model,
            subModel: taskPayload.model,
            duration: taskPayload.duration,
            mode: taskPayload.mode,
            sound: taskPayload.sound,
            resolution: taskPayload.resolution,
            hasReferenceVideo: false,
            onSuccess: (statusData) => {
              delete pollIntervalsRef.current[pollKey];
              setRows((prev) => {
                const n = [...prev];
                const rowIdx = n.findIndex(s => s.id === task.shotId);
                if (rowIdx !== -1) {
                  const currentRow = n[rowIdx];
                  const historyItem = wanxRequest ? {
                    type: "video",
                    url: statusData.videoUrl,
                    prompt: task.prompt,
                    provider: "wanx",
                    model: "wanx",
                    sub_model: wanxRequest.model,
                    duration: wanxRequest.duration,
                    resolution: wanxRequest.resolution,
                    ratio: wanxRequest.ratio,
                    mode: "first_last_frame",
                    audio: true,
                    sound: "on",
                    watermark: false,
                    promptExtend: true,
                    wanxMedia: taskPayload.wanxMedia.map((item) => ({ ...item })),
                    created_at: Date.now(),
                  } : null;
                  n[rowIdx] = {
                    ...currentRow,
                    isGeneratingVideo: false,
                    generated_video: statusData.videoUrl,
                    video_prompt: task.prompt,
                    video_task_status: "success",
                    video_task_error: null,
                    ...(historyItem ? {
                      history_list: [...(currentRow.history_list || []), historyItem].slice(0, 10),
                    } : {}),
                  };
                }
                performAutoSave?.(n);
                return n;
              });
              resolve(statusData);
            },
            onError: (errorMessage) => {
              delete pollIntervalsRef.current[pollKey];
              const message = errorMessage || `视频 ${idx + 1} 生成失败`;
              setRows((prev) => prev.map((row) => row.id === task.shotId
                ? { ...row, isGeneratingVideo: false, video_task_status: "failed", video_task_error: message }
                : row));
              reject(new Error(message));
            },
          });
          pollIntervalsRef.current[pollKey] = stop;
        });
      });

      // 等待所有视频生成完成
      const settled = await Promise.allSettled(videoFutures);
      const failed = settled
        .map((result, index) => ({ result, task: videoTasks[index] }))
        .filter(({ result }) => result.status === "rejected");
      if (failed.length > 0) {
        setRows((prev) => prev.map((row) => {
          const failedTask = failed.find(({ task }) => task.shotId === row.id);
          if (!failedTask) return row;
          return {
            ...row,
            isGeneratingVideo: false,
            video_task_status: "failed",
            video_task_error: failedTask.result.reason?.message || "序列视频生成失败",
          };
        }));
        const firstMessage = failed[0].result.reason?.message || "请查看失败分镜";
        throw new Error(`${failed.length}/${videoTasks.length} 个序列视频生成失败：${firstMessage}`);
      }

      // 清除所有生成中状态
      setRows((prev) => prev.map((r) => ({ ...r, isGeneratingVideo: false })));

      return { success: true, totalVideos: videoTasks.length };
    } catch (e) {
      // 失败，清除状态
      setRows((prev) => prev.map((r) => ({ ...r, isGeneratingVideo: false })));
      throw e;
    }
  }, [setRows, performAutoSave, clearPollInterval, projectId, episodeId]);

  /**
   * 构建序列视频提示词
   * 包含当前分镜和下一个分镜的所有对话和旁白
   */
  const buildSequenceVideoPrompt = (currentShot, nextShot, index, total) => {
    const parts = [];

    // 视频序号
    parts.push(`【序列视频 ${index + 1}/${total - 1}】`);

    // 当前分镜的画面描述
    if (currentShot.visual_content || currentShot.visualContent) {
      parts.push(`画面: ${currentShot.visual_content || currentShot.visualContent}`);
    }

    // 当前分镜的动作
    if (currentShot.action) {
      parts.push(`动作: ${currentShot.action}`);
    }

    // 当前分镜的对话和旁白（从 content 字段获取）
    const currentContent = currentShot.content || '';
    if (currentContent && currentContent.trim()) {
      parts.push(`【对话/旁白】\n${currentContent}`);
    }

    // 下一个分镜的对话和旁白（从 content 字段获取）
    const nextContent = nextShot?.content || '';
    if (nextContent && nextContent.trim()) {
      parts.push(`【接续对话/旁白】\n${nextContent}`);
    }

    // 连续性要求
    parts.push(`【连续性要求】视频第一帧必须与起始图完全一致，最后一帧必须与结束图完全一致，确保动作流畅过渡。`);

    // 音频生成要求（只有在有对话/旁白时才添加）
    if (currentContent || nextContent) {
      parts.push(`【音频要求】请根据上述对话/旁白生成对应的语音，确保每句台词和旁白都清晰可听，与剧本内容完全一致。`);
    }

    return parts.join('\n');
  };

  /**
   * 一键生视频：为单个分镜提交视频生成任务
   * viduConfig 包含 Vidu 专属参数 { resolution, audio, audioType, bgm, offPeak, movementAmplitude, voiceId, seed, duration }
   */
  const handleGenerateViduVideo = useCallback(async (index, rows, currentRatio, characterOptions, sceneOptions, propOptions, provider = "vidu", model = "viduq3-pro", viduConfig = {}) => {
    if (!videoEnabledRef.current) {
      throw new Error("视频区域已隐藏，请先开启视频栏");
    }
    provider = normalizeVideoProvider(provider);
    model = normalizeVideoModel(provider, model);
    const row = rows[index];
    const shotId = row.id;

    if (!row.generated_image) {
      throw new Error(`分镜 ${index + 1} 没有渲染图，跳过`);
    }

    const finalPrompt = row.video_prompt || row.image_prompt || row.visual_content || "";

    // 时长优先级：viduConfig.duration > row.time
    const rawDuration = viduConfig.duration
      ?? (parseInt((row.time || "5s").replace("s", ""), 10) || 5);
    const durationValue = String(provider === "wanx"
      ? normalizeWanxVideoDuration(rawDuration, 5)
      : rawDuration);

    // 设置生成中状态
    setRows((prev) => {
      const n = [...prev];
      const idx = n.findIndex(s => s.id === shotId);
      if (idx === -1) return n;
      n[idx] = { ...n[idx], isGeneratingVideo: true, video_task_error: null };
      return n;
    });

    try {
      const taskPayload = {
        shotId,
        provider,
        model,
        prompt: finalPrompt,
        mode: "image_to_video",
        startImageUrl: row.generated_image,
        duration: durationValue,
        aspectRatio: currentRatio,
        resolution: viduConfig.resolution || "720p",
      };

      if (provider === "wanx") {
        const wanxRequest = validateWanxVideoRequest({
          model,
          prompt: finalPrompt,
          media: [{ type: WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, url: row.generated_image }],
          mode: "img2video",
          duration: durationValue,
          resolution: viduConfig.resolution || "1080P",
          ratio: currentRatio,
          seed: viduConfig.seed,
        });
        taskPayload.model = wanxRequest.model;
        taskPayload.prompt = wanxRequest.prompt;
        taskPayload.duration = wanxRequest.duration;
        taskPayload.resolution = wanxRequest.resolution;
        taskPayload.ratio = wanxRequest.ratio;
        taskPayload.aspectRatio = wanxRequest.ratio;
        taskPayload.audio = viduConfig.audio !== false;
        taskPayload.sound = taskPayload.audio ? "on" : "off";
        taskPayload.watermark = viduConfig.watermark === true;
        taskPayload.promptExtend = viduConfig.promptExtend !== false;
        taskPayload.mode = "first_frame";
        taskPayload.generationMode = "first_frame";
        taskPayload.wanxMedia = wanxRequest.media.map((item) => ({
          type: item.type,
          url: item.url,
        }));
        taskPayload.startImageUrl = null;
        taskPayload.endImageUrl = null;
        taskPayload.referenceUrls = [];
      }

      // Vidu 专属参数
      if (provider === "vidu") {
        if (viduConfig.audio != null) taskPayload.audio = viduConfig.audio;
        if (viduConfig.audioType) taskPayload.audioType = viduConfig.audioType;
        if (viduConfig.bgm != null) taskPayload.bgm = viduConfig.bgm;
        if (viduConfig.offPeak != null) taskPayload.offPeak = viduConfig.offPeak;
        if (viduConfig.movementAmplitude) taskPayload.movementAmplitude = viduConfig.movementAmplitude;
        if (viduConfig.voiceId) taskPayload.voiceId = viduConfig.voiceId;
        if (viduConfig.seed != null) taskPayload.seed = viduConfig.seed;
      }

      const { taskId, provider: returnedProvider } = await submitVideoTask(taskPayload);

      const effectiveProvider = returnedProvider || provider;
      setRows((prev) => prev.map((item) => item.id === shotId ? {
        ...item,
        isGeneratingVideo: videoEnabledRef.current,
        video_task_id: taskId,
        video_task_status: "polling",
        video_provider: effectiveProvider,
        ...(provider === "wanx" ? {
          video_model: "wanx",
          video_sub_model: taskPayload.model,
          video_wan_mode: "first_frame",
          video_wan_duration: taskPayload.duration,
          video_wan_resolution: taskPayload.resolution,
          video_wan_ratio: taskPayload.ratio,
          video_wan_audio: taskPayload.audio,
          video_wan_prompt_extend: taskPayload.promptExtend,
        } : {}),
        video_task_error: null,
      } : item));

      if (!videoEnabledRef.current) {
        return { taskId, provider: effectiveProvider, suspended: true };
      }

      // 轮询视频状态
      return new Promise((resolve, reject) => {
        const pollKey = `vid_${shotId}`;
        clearPollInterval(pollKey);

        const stop = startVideoPolling(effectiveProvider, taskId, {
          interval: 5000,
          projectId: currentProjectIdFromPath(),
          episodeId,
          prompt: finalPrompt,
          model: taskPayload.model,
          subModel: taskPayload.model,
          duration: taskPayload.duration,
          mode: taskPayload.mode,
          sound: taskPayload.sound,
          resolution: taskPayload.resolution,
          hasReferenceVideo: false,
          onSuccess: (statusData) => {
            delete pollIntervalsRef.current[pollKey];
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;

              // 构建完整的 history item，包含费用计算所需参数
              const historyItem = {
                type: "video",
                url: statusData.videoUrl,
                prompt: finalPrompt,
                model: model,
                created_at: Date.now(),
              };

              // 添加视频生成参数（用于费用计算）
              const currentRow = n[idx];
              if (provider === "vidu") {
                historyItem.sub_model = model;
                historyItem.duration = parseInt(viduConfig.duration || currentRow.video_vidu_duration || "5");
                historyItem.resolution = viduConfig.resolution || currentRow.video_resolution || "720p";
              } else if (provider === "wangsuSora") {
                historyItem.sub_model = model;
                historyItem.duration = parseInt(viduConfig.duration || currentRow.video_wangsu_duration || "4");
                historyItem.resolution = viduConfig.resolution || currentRow.video_resolution || "1280x720";
              } else if (provider === "minimax") {
                historyItem.sub_model = model;
                historyItem.duration = parseInt(viduConfig.duration || currentRow.video_duration || "6");
                historyItem.resolution = viduConfig.resolution || currentRow.video_resolution || "768p";
              } else if (provider === "wanx") {
                historyItem.provider = "wanx";
                historyItem.model = "wanx";
                historyItem.sub_model = taskPayload.model;
                historyItem.duration = taskPayload.duration;
                historyItem.resolution = taskPayload.resolution;
                historyItem.mode = "first_frame";
                historyItem.audio = taskPayload.audio;
                historyItem.sound = taskPayload.sound;
                historyItem.ratio = taskPayload.ratio;
                historyItem.watermark = taskPayload.watermark;
                historyItem.promptExtend = taskPayload.promptExtend;
                historyItem.wanxMedia = taskPayload.wanxMedia.map((item) => ({ ...item }));
              } else {
                // 其他模型：通用处理
                historyItem.sub_model = model;
                historyItem.duration = parseInt(viduConfig.duration || currentRow.video_duration || "5");
              }

              n[idx] = {
                ...n[idx],
                isGeneratingVideo: false,
                generated_video: statusData.videoUrl,
                video_task_status: "success",
                video_task_error: null,
                history_list: [...(n[idx].history_list || []), historyItem].slice(0, 10),
              };
              performAutoSave?.(n);
              return n;
            });
            resolve(statusData);
          },
          onError: (errorMessage) => {
            delete pollIntervalsRef.current[pollKey];
            const message = errorMessage || `分镜 ${index + 1} 视频生成失败`;
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx === -1) return prev;
              n[idx] = {
                ...n[idx],
                isGeneratingVideo: false,
                video_task_status: "failed",
                video_task_error: message,
              };
              return n;
            });
            reject(new Error(message));
          },
        });

        pollIntervalsRef.current[pollKey] = stop;
      });
    } catch (e) {
      setRows((prev) => {
        const n = [...prev];
        const idx = n.findIndex(s => s.id === shotId);
        if (idx === -1) return n;
        n[idx] = {
          ...n[idx],
          isGeneratingVideo: false,
          video_task_status: "failed",
          video_task_error: e.message || "视频提交失败",
        };
        return n;
      });
      throw e;
    }
  }, [setRows, performAutoSave, clearPollInterval, buildRefList, episodeId]);

  /**
   * 批量生成普通图片（非宫格图）
   * 用于首尾帧模式，每个分镜生成1张普通图片
   */
  const handleBatchGenerateNormalImages = useCallback(async (
    rows,
    currentStyle,
    currentRatio,
    buildRefListFn,
    characterOptions,
    sceneOptions,
    propOptions
  ) => {
    if (!rows || rows.length === 0) {
      throw new Error("没有分镜数据");
    }

    console.log(`[BatchGenerate] 开始批量生成, 共 ${rows.length} 个分镜`);
    rows.forEach((r, i) => {
      const prompt = r.image_prompt || r.visual_content || r.action;
      console.log(`[BatchGenerate] 分镜 ${i + 1}: id=${r.id}, hasPrompt=${!!prompt}, prompt="${(prompt || '').substring(0, 30)}..."`);
    });

    const results = {
      successCount: 0,
      failedCount: 0,
      errorMessages: [],
    };

    // 并发控制：每次最多3个
    const concurrency = 3;
    for (let i = 0; i < rows.length; i += concurrency) {
      const batch = rows.slice(i, Math.min(i + concurrency, rows.length));
      console.log(`[BatchGenerate] 处理批次 ${i / concurrency + 1}, 索引 ${i} ~ ${i + batch.length - 1}`);

      await Promise.allSettled(
        batch.map(async (row, batchIndex) => {
          const actualIndex = i + batchIndex;
          const shotId = row.id;
          try {
            console.log(`[BatchGenerate] 开始生成分镜 ${actualIndex + 1}, shotId=${shotId}`);
            await handleGenerateImage(
              actualIndex,
              rows,
              currentStyle,
              currentRatio,
              buildRefListFn,
              characterOptions,
              sceneOptions,
              propOptions
            );
            results.successCount++;
            console.log(`[BatchGenerate] 分镜 ${actualIndex + 1} 提交成功`);
          } catch (error) {
            results.failedCount++;
            results.errorMessages.push(`分镜 ${actualIndex + 1}: ${error.message}`);
            console.error(`[BatchGenerate] 分镜 ${actualIndex + 1} 生成失败:`, error);

            // 清除失败分镜的生成中状态（用 shotId 定位）
            setRows((prev) => {
              const n = [...prev];
              const idx = n.findIndex(s => s.id === shotId);
              if (idx !== -1) {
                n[idx] = { ...n[idx], isGenerating: false };
              }
              return n;
            });
          }
        })
      );
    }

    return results;
  }, [handleGenerateImage, setRows]);

  /**
   * 页面加载时恢复 polling 状态的图片任务
   * 在分镜列表加载完 rows 后调用
   */
  const resumeImagePolling = useCallback((rows) => {
    rows.forEach((row) => {
      if (row.image_task_status === "polling" && row.image_task_id && row.id) {
        const shotId = row.id;
        const pollKey = `img_resume_${shotId}`;
        if (pollIntervalsRef.current[pollKey]) return;

        let isRunning = true;
        let timeoutId = null;
        let errorCount = 0;

        const poll = async () => {
          if (!isRunning) return;
          try {
            const data = await queryImageTaskStatus(shotId);
            errorCount = 0;

            if (data.status === "success") {
              isRunning = false;
              delete pollIntervalsRef.current[pollKey];
              setRows((prev) => {
                const n = [...prev];
                const idx = n.findIndex(s => s.id === shotId);
                if (idx === -1) return prev;
                const row = n[idx];
                const nextRow = data.generatedImage
                  ? syncGeneratedImageForRow(row, data.generatedImage, {
                      source: "resume-polling",
                      model: row.image_provider || "image",
                      addHistory: false,
                      clearLastFrame: true,
                    })
                  : row;
                n[idx] = {
                  ...nextRow,
                  isGenerating: false,
                  image_task_status: "success",
                  image_candidates: data.imageCandidates?.length > 0
                    ? data.imageCandidates
                    : nextRow.image_candidates,
                  continuity: data.continuity || nextRow.continuity,
                  openai_generation: data.openai_generation || nextRow.openai_generation,
                };
                performAutoSave?.(n);
                return n;
              });
            } else if (data.status === "failed" || data.status === "cancelled") {
              isRunning = false;
              delete pollIntervalsRef.current[pollKey];
              setRows((prev) => {
                const n = [...prev];
                const idx = n.findIndex(s => s.id === shotId);
                if (idx === -1) return prev;
                n[idx] = { ...n[idx], isGenerating: false, image_task_status: data.status };
                return n;
              });
            } else {
              // 还在 polling，继续等待
              timeoutId = setTimeout(poll, 3000);
            }
          } catch (e) {
            errorCount++;
            if (errorCount >= 5) {
              isRunning = false;
              delete pollIntervalsRef.current[pollKey];
              setRows((prev) => {
                const n = [...prev];
                const idx = n.findIndex(s => s.id === shotId);
                if (idx === -1) return prev;
                n[idx] = { ...n[idx], isGenerating: false };
                return n;
              });
            } else {
              timeoutId = setTimeout(poll, 3000);
            }
          }
        };

        pollIntervalsRef.current[pollKey] = () => {
          isRunning = false;
          if (timeoutId) clearTimeout(timeoutId);
        };

        // 标记为生成中，然后开始轮询
        setRows((prev) => {
          const n = [...prev];
          const idx = n.findIndex(s => s.id === shotId);
          if (idx !== -1) n[idx] = { ...n[idx], isGenerating: true };
          return n;
        });
        poll();
      }
    });
  }, [setRows, performAutoSave]);

  /**
   * 当后端返回真实 ID 后，将 pollIntervalsRef 中旧 key 迁移到新 key
   * @param {Array<{oldId, newId}>} idMap - 旧 ID → 新 ID 映射
   */
  const remapPollKeys = useCallback((idMap) => {
    if (!idMap || idMap.length === 0) return;
    const prefixes = ['img_', 'vid_', 'prompt_', 'img_batch_', 'img_resume_'];
    for (const { oldId, newId } of idMap) {
      for (const prefix of prefixes) {
        const oldKey = `${prefix}${oldId}`;
        const val = pollIntervalsRef.current[oldKey];
        if (val !== undefined) {
          const newKey = `${prefix}${newId}`;
          pollIntervalsRef.current[newKey] = val;
          delete pollIntervalsRef.current[oldKey];
        }
      }
    }
  }, []);

  /**
   * 组图生成（wan2.7 enable_sequential）
   * 将多个分镜的 imagePrompt 合并，一次生成角色一致的连续图片，按顺序回填。
   */
  const handleBatchSequentialImages = useCallback(async (
    targetRows,
    targetIndices,
    buildRefListFn,
    characterOptions,
    sceneOptions,
    propOptions,
    aspectRatio,
    style
  ) => {
    if (!targetRows || targetRows.length === 0) {
      throw new Error("没有分镜数据");
    }
    if (targetRows.length > 12) {
      throw new Error("组图最多支持12个分镜");
    }

    // 1. 收集所有分镜的参考图（去重）
    const refUrlSet = new Set();
    const refBuilder = buildRefListFn || buildRefList;
    for (const row of targetRows) {
      const charRefs = refBuilder(row.refCharacterIds, characterOptions, row.refCharacterVariantIndexes);
      const sceneRefs = refBuilder(row.refSceneIds, sceneOptions, row.refSceneVariantIndexes);
      const propRefs = refBuilder(row.refPropIds, propOptions, row.refPropVariantIndexes);
      [...charRefs, ...sceneRefs, ...propRefs].forEach(ref => {
        if (ref.base64) refUrlSet.add(ref.base64);
      });
    }

    // 2. 设置所有目标分镜为生成中
    const shotIds = targetRows.map(r => r.id).filter(Boolean);
    setRows(prev => {
      const n = [...prev];
      for (const idx of targetIndices) {
        if (n[idx]) n[idx] = { ...n[idx], isGenerating: true };
      }
      return n;
    });

    try {
      // 3. 提交统一 AI 任务，由后端 Worker 执行 Wanx 连续组图并回填分镜
      const { result } = await runTaskToCompletion("STORYBOARD_SEQUENTIAL_IMAGES", {
          shotIds,
          referenceImageUrls: Array.from(refUrlSet).slice(0, 9),
          aspectRatio: aspectRatio || "16:9",
          style: style || "",
        }, {
          source: "sequential-images",
          projectId: targetRows[0]?.projectId,
          episodeId: targetRows[0]?.episodeId,
          timeoutMs: 30 * 60 * 1000,
      });
      const results = result?.results || result?.result?.results || [];

      // 4. 回填结果到 rows
      setRows(prev => {
        const n = [...prev];
        for (const result of results) {
          const idx = n.findIndex(s => s.id === result.shotId);
          if (idx !== -1 && result.imageUrl) {
            n[idx] = {
              ...syncGeneratedImageForRow(n[idx], result.imageUrl, {
                source: "sequential-images",
                model: "wanx",
                clearLastFrame: true,
              }),
              isGenerating: false,
            };
          }
        }
        // 清除未回填的生成状态
        for (const idx of targetIndices) {
          if (n[idx]?.isGenerating) {
            n[idx] = { ...n[idx], isGenerating: false };
          }
        }
        performAutoSave?.(n);
        return n;
      });

      return { successCount: results.length, failedCount: targetRows.length - results.length };
    } catch (error) {
      // 清除生成状态
      setRows(prev => {
        const n = [...prev];
        for (const idx of targetIndices) {
          if (n[idx]) n[idx] = { ...n[idx], isGenerating: false };
        }
        return n;
      });
      throw error;
    }
  }, [buildRefList, setRows, performAutoSave]);

  return {
    // 核心方法
    handleGenerateImage,
    handleGenerateImageBatch,
    handleGenerateVideo,
    handleAutoVideoPrompt,
    handleRedraw,
    handleBatchGenerateGrids,
    handleBatchGenerateNormalImages,
    handleBatchGenerateFirstLastFrames,
    handleBatchGenerateSequenceVideos,
    handleGenerateViduVideo,
    handleBatchSequentialImages,

    // 轮询恢复（页面加载时调用）
    resumeVideoPolling,
    resumeImagePolling,

    // 工具方法
    buildRefList,
    pollTaskStatus,
    remapPollKeys,

    // 清理方法
    clearPollInterval,
    clearAllPollIntervals,
    suspendVideoPolling,
    stopVideoGeneration,
    stopImageGeneration,
  };
};

export default useAIGeneration;
